import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Plus, Search, Tag } from 'lucide-react';
import { getEmailApiBaseUrl } from '../../../utils/emailApi';
import LoadingSpinner from '../../common/LoadingSpinner';
import {
  gymMarketingCardInner,
  gymModalInput,
  gymModalLabel,
  gymSocialBtnPrimary,
  gymSocialBtnSecondary,
  gymSocialCard,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';

const PAGE_SIZE = 25;

function memberHasConsent(member) {
  return member.marketing_opt_in !== false && member.marketing_opt_in !== 0;
}

export default function GymEmailTagsPanel({ api }) {
  const { t } = useTranslation();
  const emailApi = getEmailApiBaseUrl();
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState('');
  const [listFilter, setListFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [tagBusyKey, setTagBusyKey] = useState(null);

  const customTags = tags.filter((tag) => !tag.is_system);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const tagsRes = await api.get('gym_email_tags.php?action=list', { baseURL: emailApi });
      let membersRes;
      try {
        membersRes = await api.get('gym_email_tags.php?action=members', { baseURL: emailApi });
      } catch (membersErr) {
        if (membersErr.response?.status !== 400) {
          throw membersErr;
        }
        membersRes = await api.get('gym_email_tags.php?action=opt_in_members', { baseURL: emailApi });
      }
      setTags(tagsRes.data.tags || []);
      setMembers(membersRes.data.members || []);
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.tags.loadError'));
    } finally {
      setLoading(false);
    }
  }, [api, emailApi, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) {
      return;
    }
    setSaving(true);
    try {
      await api.post('gym_email_tags.php?action=create', { name }, { baseURL: emailApi });
      setNewName('');
      toast.success(t('gym.email.tags.created'));
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.tags.createError'));
    } finally {
      setSaving(false);
    }
  };

  const handleBackfill = async () => {
    try {
      const res = await api.post('gym_email_tags.php?action=backfill', {}, { baseURL: emailApi });
      toast.success(t('gym.email.tags.backfillDone', { count: res.data.synced_members ?? 0 }));
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.tags.backfillError'));
    }
  };

  const toggleMemberTag = async (memberId, tag, isAssigned) => {
    const busyKey = `${memberId}-${tag.id}`;
    setTagBusyKey(busyKey);
    try {
      if (isAssigned) {
        await api.post(
          'gym_email_tags.php?action=unassign',
          { user_id: memberId, tag_id: tag.id },
          { baseURL: emailApi }
        );
      } else {
        await api.post(
          'gym_email_tags.php?action=assign',
          { user_id: memberId, tag_id: tag.id },
          { baseURL: emailApi }
        );
      }
      setMembers((prev) => prev.map((member) => {
        if (member.id !== memberId) {
          return member;
        }
        const current = member.custom_tags || [];
        const next = isAssigned
          ? current.filter((item) => item.id !== tag.id)
          : [...current, { id: tag.id, name: tag.name }];
        return { ...member, custom_tags: next, has_custom_tags: next.length > 0 };
      }));
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.email.tags.assignError'));
    } finally {
      setTagBusyKey(null);
    }
  };

  const visibleMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter((member) => {
      if (listFilter === 'consent' && !memberHasConsent(member)) {
        return false;
      }
      if (listFilter === 'untagged' && (member.custom_tags || []).length > 0) {
        return false;
      }
      if (!q) {
        return true;
      }
      return String(member.name || '').toLowerCase().includes(q)
        || String(member.email || '').toLowerCase().includes(q);
    });
  }, [members, query, listFilter]);

  const pageCount = Math.max(1, Math.ceil(visibleMembers.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageMembers = visibleMembers.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className={`${gymSocialCard} ${gymMarketingCardInner}`}>
        <form onSubmit={handleCreate} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="block min-w-0 flex-1">
            <span className={gymModalLabel}>{t('gym.email.tags.newName')}</span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className={`mt-2 w-full ${gymModalInput}`}
              placeholder={t('gym.email.tags.newNamePlaceholder')}
              maxLength={120}
            />
          </label>
          <button type="submit" disabled={saving} className={`${gymSocialBtnPrimary} justify-center`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {t('gym.email.tags.add')}
          </button>
          <button type="button" onClick={handleBackfill} className={`${gymSocialBtnSecondary} text-sm justify-center`}>
            {t('gym.email.tags.backfill')}
          </button>
        </form>
      </div>

      <div className="min-w-0">
        <div className={`${gymSocialCard} ${gymMarketingCardInner} space-y-4`}>
          <div>
            <h2 className={`text-lg font-semibold ${gymSocialTitleText}`}>{t('gym.email.tags.membersTitle')}</h2>
            <p className={`text-sm mt-1 leading-relaxed ${gymSocialMutedText}`}>{t('gym.email.tags.membersHint')}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
              <input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                className={`w-full pl-9 ${gymModalInput}`}
                placeholder={t('gym.email.tags.membersSearch')}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                ['all', 'gym.email.tags.filterAll'],
                ['consent', 'gym.email.tags.filterConsent'],
                ['untagged', 'gym.email.tags.filterUntagged'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setListFilter(key);
                    setPage(0);
                  }}
                  className={`rounded-full px-3 py-1 text-sm ${
                    listFilter === key
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white/10 text-white/80'
                  }`}
                >
                  {t(label)}
                </button>
              ))}
            </div>
          </div>
          {customTags.length === 0 && (
            <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.email.tags.membersNoTags')}</p>
          )}
          {members.length === 0 ? (
            <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.email.tags.membersEmpty')}</p>
          ) : visibleMembers.length === 0 ? (
            <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.email.tags.membersNoMatch')}</p>
          ) : (
            <>
            {visibleMembers.length > PAGE_SIZE && (
              <div className="flex items-center justify-between gap-3">
                <p className={`text-xs ${gymSocialMutedText}`}>
                  {t('gym.email.tags.pageStatus', {
                    from: safePage * PAGE_SIZE + 1,
                    to: Math.min(visibleMembers.length, (safePage + 1) * PAGE_SIZE),
                    total: visibleMembers.length,
                  })}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={safePage === 0}
                    onClick={() => setPage(safePage - 1)}
                    className={`${gymSocialBtnSecondary} text-sm disabled:opacity-40`}
                  >
                    {t('gym.email.tags.pagePrev')}
                  </button>
                  <button
                    type="button"
                    disabled={safePage >= pageCount - 1}
                    onClick={() => setPage(safePage + 1)}
                    className={`${gymSocialBtnSecondary} text-sm disabled:opacity-40`}
                  >
                    {t('gym.email.tags.pageNext')}
                  </button>
                </div>
              </div>
            )}
            <ul className="divide-y divide-white/10">
              {pageMembers.map((member) => (
                <MemberTagRow
                  key={member.id}
                  member={member}
                  customTags={customTags}
                  tagBusyKey={tagBusyKey}
                  onToggle={toggleMemberTag}
                />
              ))}
            </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MemberTagRow({ member, customTags, tagBusyKey, onToggle }) {
  const { t } = useTranslation();
  const assignedIds = new Set((member.custom_tags || []).map((tag) => tag.id));
  const hasConsent = memberHasConsent(member);

  return (
    <li className="grid grid-cols-1 items-center gap-2 py-2 sm:grid-cols-[minmax(8rem,1fr)_minmax(10rem,1.3fr)_7rem_minmax(8rem,1.2fr)] sm:gap-3">
      <p className="truncate font-medium text-gray-900 dark:text-white">{member.name}</p>
      <p className="truncate text-xs text-gray-500 dark:text-white/60">{member.email || '—'}</p>
      <p className="text-xs text-gray-500 dark:text-white/60">
        {hasConsent ? t('gym.email.tags.consentYes') : t('gym.email.tags.consentNo')}
      </p>
      <div className="flex flex-wrap gap-1.5 sm:justify-end">
        {customTags.map((tag) => {
          const active = assignedIds.has(tag.id);
          const busy = tagBusyKey === `${member.id}-${tag.id}`;
          return (
            <button
              key={tag.id}
              type="button"
              disabled={busy}
              onClick={() => onToggle(member.id, tag, active)}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                active
                  ? 'border-indigo-400 bg-indigo-100 text-indigo-900 dark:border-[#cba5fa]/50 dark:bg-indigo-900/50 dark:text-indigo-100'
                  : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400 dark:border-white/20 dark:bg-gray-900/60 dark:text-white/75'
              }`}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Tag className="h-3 w-3 opacity-70" />}
              {tag.name}
            </button>
          );
        })}
      </div>
    </li>
  );
}
